import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchProject } from "../../services/projectService";

// Async thunk for fetching project data
// This replaces RTK Query with Redux Thunk - no caching
export const fetchProjectAsync = createAsyncThunk(
  "project/fetchProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const projectData = await fetchProject(projectId);
      return projectData;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch project");
    }
  },
);

const initialState = {
  // Creation mode - for new project creation
  creation: {
    metadata: {
      name: "",
      shortDescription: "",
      longDescription: "",
      category: "",
      shortName: "",
      appenPartner: "",
      account: "",
      hireStartDate: "",
      predictedCloseDate: "",
      projectStatus: "",
      projectType: "",
      projectPriority: "50",
      projectManagerEmail: "",
      projectPaymentMethod: "",
    },
    settings: {
      autoApprove: false,
      requireApproval: true,
      maxWorkers: 100,
    },
    requirements: {
      paymentSetupRequired: true,
    },
    originalData: null,
  },

  // Update mode - for editing existing projects
  update: {
    metadata: {
      name: "",
      shortDescription: "",
      longDescription: "",
      category: "",
      shortName: "",
      appenPartner: "",
      account: "",
      hireStartDate: "",
      predictedCloseDate: "",
      projectStatus: "",
      projectType: "",
      projectPriority: "",
      projectManagerEmail: "",
      projectPaymentMethod: "",
    },
    settings: {
      autoApprove: false,
      requireApproval: true,
      maxWorkers: 100,
    },
    hasChanges: false,
    changedFields: {},
  },

  // Display mode - for showing project details from API
  display: {
    // Transformed data (from fromSalesforceProject)
    metadata: {
      name: "",
      shortDescription: "",
      longDescription: "",
      category: "",
      shortName: "",
      appenPartner: "",
      account: "",
      hireStartDate: "",
      predictedCloseDate: "",
      projectStatus: "",
      projectType: "",
      projectPriority: "",
      projectManagerEmail: "",
      projectPaymentMethod: "",
    },
    settings: {
      autoApprove: false,
      requireApproval: true,
      maxWorkers: 100,
    },
    // Raw Salesforce API response (untransformed)
    rawApiResponse: null,
    loading: false,
    error: null,
  },
};

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    // Creation mode actions
    updateCreationMetadata: (state, action) => {
      state.creation.metadata = {
        ...state.creation.metadata,
        ...action.payload,
      };
    },
    updateCreationSettings: (state, action) => {
      state.creation.settings = {
        ...state.creation.settings,
        ...action.payload,
      };
    },
    resetCreation: (state) => {
      state.creation = initialState.creation;
    },
    loadCreationData: (state, action) => {
      state.creation.metadata =
        action.payload.metadata || state.creation.metadata;
      state.creation.settings =
        action.payload.settings || state.creation.settings;
    },
    setCreationOriginalData: (state, action) => {
      state.creation.originalData = action.payload;
    },

    // Update mode actions
    updateUpdateMetadata: (state, action) => {
      state.update.metadata = { ...state.update.metadata, ...action.payload };
      state.update.hasChanges = true;
      state.update.changedFields = {
        ...state.update.changedFields,
        ...action.payload,
      };
    },
    updateUpdateSettings: (state, action) => {
      state.update.settings = { ...state.update.settings, ...action.payload };
      state.update.hasChanges = true;
    },
    loadUpdateData: (state, action) => {
      state.update.metadata = action.payload.metadata || state.update.metadata;
      state.update.settings = action.payload.settings || state.update.settings;
      state.update.hasChanges = false;
      state.update.changedFields = {};
    },
    clearUpdateChanges: (state) => {
      state.update.hasChanges = false;
      state.update.changedFields = {};
    },
    resetUpdate: (state) => {
      state.update = initialState.update;
    },

    // Display mode actions
    setDisplayData: (state, action) => {
      state.display.metadata =
        action.payload.metadata || state.display.metadata;
      state.display.settings =
        action.payload.settings || state.display.settings;
      state.display.rawApiResponse =
        action.payload.rawApiResponse || state.display.rawApiResponse;
      state.display.loading = false;
      state.display.error = null;
    },
    setDisplayLoading: (state, action) => {
      state.display.loading = action.payload;
    },
    setDisplayError: (state, action) => {
      state.display.error = action.payload;
      state.display.loading = false;
    },
    resetDisplay: (state) => {
      state.display = initialState.display;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchProjectAsync - loading state
      .addCase(fetchProjectAsync.pending, (state) => {
        state.display.loading = true;
        state.display.error = null;
      })
      // fetchProjectAsync - success
      .addCase(fetchProjectAsync.fulfilled, (state, action) => {
        state.display.metadata = action.payload.metadata || {};
        state.display.settings = action.payload.settings || {};
        state.display.rawApiResponse = action.payload.rawApiResponse || null;
        state.display.loading = false;
        state.display.error = null;
      })
      // fetchProjectAsync - error
      .addCase(fetchProjectAsync.rejected, (state, action) => {
        state.display.loading = false;
        state.display.error = action.payload || "Failed to load project";
      });
  },
});

export const {
  updateCreationMetadata,
  updateCreationSettings,
  resetCreation,
  loadCreationData,
  setCreationOriginalData,
  updateUpdateMetadata,
  updateUpdateSettings,
  loadUpdateData,
  clearUpdateChanges,
  resetUpdate,
  setDisplayData,
  setDisplayLoading,
  setDisplayError,
  resetDisplay,
} = projectSlice.actions;

export default projectSlice.reducer;
